import { getSupabase } from '../../components/supabaseClient';
import { tableName } from '../../components/env';
import { normalizeImportedTime } from './normalizeTime';

// Matches patientImport/findDuplicates.js — PostgREST .in() lists get unwieldy
// past a couple of hundred values.
const CHUNK = 200;

export function normalizeNameKey(name) {
  return String(name == null ? '' : name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

// Two keys per appointment, so a file row still matches an existing row when only
// one of the two carries a chart number. An empty time is a real value here: it
// makes a timeless row collide only with another timeless row on the same day.
export function appointmentKeys(row) {
  const date = row?.date_;
  if (!date) return [];
  const time = normalizeImportedTime(row?.time_) || '';
  const keys = [];
  const ehr = String(row?.EHR_ID == null ? '' : row.EHR_ID).trim().toLowerCase();
  if (ehr) keys.push(`ehr|${ehr}|${date}|${time}`);
  const name = normalizeNameKey(row?.patient_name);
  if (name) keys.push(`name|${name}|${date}|${time}`);
  return keys;
}

// Read back only the days the file actually touches, not the whole table.
export async function fetchExistingAppointments(dates, getClient = getSupabase) {
  const existing = new Set();
  const unique = [...new Set((dates || []).filter(Boolean))];
  if (unique.length === 0) return existing;

  const client = await getClient();
  for (let i = 0; i < unique.length; i += CHUNK) {
    const chunk = unique.slice(i, i + CHUNK);
    // "EHR_ID" is quoted: notes-c capitalises it, unlike the roster tables.
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await client
      .from(tableName)
      .select('patient_name,date_,time_,"EHR_ID"')
      .in('date_', chunk);
    if (error || !data) continue;
    data.forEach((existingRow) => {
      appointmentKeys(existingRow).forEach((k) => existing.add(k));
    });
  }
  return existing;
}

// Split the file's rows into what to insert and what is already on the schedule.
// Rows are also deduplicated against each other, so one file cannot double-book.
export function partitionDuplicates(rows, existingKeys) {
  const seen = new Set(existingKeys || []);
  const toImport = [];
  const duplicates = [];

  (rows || []).forEach((row) => {
    const keys = appointmentKeys(row);
    // No keys means nothing to compare on — keep the row rather than drop it.
    if (keys.length > 0 && keys.some((k) => seen.has(k))) {
      duplicates.push(row);
      return;
    }
    keys.forEach((k) => seen.add(k));
    toImport.push(row);
  });

  return { toImport, duplicates };
}
